/**
 * PrepMasterListBlock — Éditeur de la Liste maîtresse "Préparation jour J".
 *
 * Liste GLOBALE et PERMANENTE, indépendante des réservations, du calendrier
 * et de Lodgify. Sert de check-list papier générique pour préparer le refuge.
 *
 * Sections :
 *   1. Linge prêt
 *   2. Panier ménage prêt
 *   3. Préparation petit-déjeuner
 *   4. Troisième lit prêt (Lit parapluie + Lit déplié)
 *
 * Blanchisserie prête : SUPPRIMÉE (spec Fév 2026).
 */

import { useEffect, useState } from "react";
import { rgApi } from "@/lib/rgApi";
import "./rg-theme.css";
import "./prep-master-list.css";

function uid() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function move(arr, from, to) {
  if (to < 0 || to >= arr.length) return arr;
  const copy = [...arr];
  const [item] = copy.splice(from, 1);
  copy.splice(to, 0, item);
  return copy;
}

export default function PrepMasterListBlock() {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [openIds, setOpenIds] = useState({});
  const [preview, setPreview] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const d = await rgApi.getPrepMasterList();
      setSections(d.sections || []);
      // Ouvrir toutes les sections par défaut
      const initOpen = {};
      (d.sections || []).forEach((s) => { initOpen[s.id] = true; });
      setOpenIds(initOpen);
      setDirty(false);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }

  function markDirty() { setDirty(true); setSaved(false); }

  function toggleSection(id) {
    setOpenIds((o) => ({ ...o, [id]: !o[id] }));
  }

  function renameSection(idx, title) {
    setSections((prev) => prev.map((s, i) => (i === idx ? { ...s, title } : s)));
    markDirty();
  }
  function moveSection(idx, dir) {
    setSections((prev) => move(prev, idx, idx + dir));
    markDirty();
  }
  function deleteSection(idx) {
    if (!confirm(`Supprimer définitivement la section « ${sections[idx].title} » ?`)) return;
    setSections((prev) => prev.filter((_, i) => i !== idx));
    markDirty();
  }
  function addSection() {
    const s = { id: uid(), title: "Nouvelle section", groups: [{ title: "", items: [] }] };
    setSections((prev) => [...prev, s]);
    setOpenIds((o) => ({ ...o, [s.id]: true }));
    markDirty();
  }

  function renameGroup(secIdx, gIdx, title) {
    setSections((prev) => prev.map((s, i) => {
      if (i !== secIdx) return s;
      const groups = s.groups.map((g, j) => (j === gIdx ? { ...g, title } : g));
      return { ...s, groups };
    }));
    markDirty();
  }
  function moveGroup(secIdx, gIdx, dir) {
    setSections((prev) => prev.map((s, i) => {
      if (i !== secIdx) return s;
      return { ...s, groups: move(s.groups, gIdx, gIdx + dir) };
    }));
    markDirty();
  }
  function deleteGroup(secIdx, gIdx) {
    const g = sections[secIdx].groups[gIdx];
    if (!confirm(`Supprimer la sous-section « ${g.title || "(sans titre)"} » et ses ${(g.items || []).length} éléments ?`)) return;
    setSections((prev) => prev.map((s, i) => {
      if (i !== secIdx) return s;
      return { ...s, groups: s.groups.filter((_, j) => j !== gIdx) };
    }));
    markDirty();
  }
  function addGroup(secIdx) {
    setSections((prev) => prev.map((s, i) => {
      if (i !== secIdx) return s;
      return { ...s, groups: [...s.groups, { title: "Nouvelle sous-section", items: [] }] };
    }));
    markDirty();
  }

  function renameItem(secIdx, gIdx, iIdx, value) {
    setSections((prev) => prev.map((s, i) => {
      if (i !== secIdx) return s;
      const groups = s.groups.map((g, j) => {
        if (j !== gIdx) return g;
        const items = g.items.map((it, k) => (k === iIdx ? value : it));
        return { ...g, items };
      });
      return { ...s, groups };
    }));
    markDirty();
  }
  function moveItem(secIdx, gIdx, iIdx, dir) {
    setSections((prev) => prev.map((s, i) => {
      if (i !== secIdx) return s;
      const groups = s.groups.map((g, j) => {
        if (j !== gIdx) return g;
        return { ...g, items: move(g.items, iIdx, iIdx + dir) };
      });
      return { ...s, groups };
    }));
    markDirty();
  }
  function deleteItem(secIdx, gIdx, iIdx) {
    setSections((prev) => prev.map((s, i) => {
      if (i !== secIdx) return s;
      const groups = s.groups.map((g, j) => {
        if (j !== gIdx) return g;
        return { ...g, items: g.items.filter((_, k) => k !== iIdx) };
      });
      return { ...s, groups };
    }));
    markDirty();
  }
  function addItem(secIdx, gIdx) {
    setSections((prev) => prev.map((s, i) => {
      if (i !== secIdx) return s;
      const groups = s.groups.map((g, j) => {
        if (j !== gIdx) return g;
        return { ...g, items: [...g.items, ""] };
      });
      return { ...s, groups };
    }));
    markDirty();
  }

  async function save() {
    setSaving(true);
    setError("");
    try {
      // Nettoyer les items vides avant envoi
      const clean = sections.map((s) => ({
        ...s,
        groups: (s.groups || []).map((g) => ({
          title: (g.title || "").trim(),
          items: (g.items || []).map((i) => (i || "").trim()).filter(Boolean),
        })),
      }));
      const d = await rgApi.putPrepMasterList({ sections: clean });
      setSections(d.sections || []);
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setSaving(false);
    }
  }

  function openPrint() {
    const w = window.open("/admin/gestion/liste-preparation/print", "_blank");
    if (!w) alert("Autorise les pop-ups pour ouvrir l'aperçu imprimable.");
  }

  async function resetDefaults() {
    if (!confirm("Restaurer la liste par défaut ? Vos modifications actuelles seront écrasées.")) return;
    setSaving(true);
    try {
      const d = await rgApi.resetPrepMasterList();
      setSections(d.sections || []);
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="rg-card"><h3 className="rg-h3">🖨️ Liste préparation jour J — à imprimer</h3><p className="rg-muted">Chargement…</p></div>;
  }

  return (
    <div className="rg-card prep-master-block" data-testid="rg-prep-master-block">
      <div className="prep-master-head">
        <h3 className="rg-h3">🖨️ Liste préparation jour J — à imprimer</h3>
        <p className="rg-muted" style={{ margin: "4px 0 12px", fontSize: 12 }}>
          Liste maîtresse GLOBALE, indépendante des réservations, du calendrier et de Lodgify.
          Modifiable, enregistrable et imprimable à tout moment.
        </p>
      </div>

      {error && <div className="rg-alert-error" data-testid="rg-prep-error">{error}</div>}
      {saved && <div className="rg-alert-success" data-testid="rg-prep-saved">✅ Liste enregistrée</div>}

      {preview ? (
        <PrepPreview sections={sections} onClose={() => setPreview(false)} onPrint={openPrint} />
      ) : (
        <>
          <div className="prep-actions">
            <button type="button" className="rg-btn ghost" onClick={() => setPreview(true)} data-testid="rg-prep-preview-btn">
              👁 Aperçu avant impression
            </button>
            <button type="button" className="rg-btn ghost" onClick={openPrint} data-testid="rg-prep-print-btn">
              🖨️ Imprimer la liste
            </button>
            <button
              type="button"
              className="rg-btn primary"
              onClick={save}
              disabled={saving || !dirty}
              data-testid="rg-prep-save-btn"
            >
              💾 {saving ? "Enregistrement…" : dirty ? "Enregistrer la liste" : "Enregistrée"}
            </button>
          </div>

          <div className="prep-sections">
            {sections.map((section, secIdx) => (
              <div className="prep-section" key={section.id} data-testid={`rg-prep-sec-${section.id}`}>
                <div className="prep-section-header">
                  <button
                    type="button"
                    className="prep-caret"
                    onClick={() => toggleSection(section.id)}
                    aria-expanded={!!openIds[section.id]}
                    data-testid={`rg-prep-sec-toggle-${section.id}`}
                  >
                    {openIds[section.id] ? "▾" : "▸"}
                  </button>
                  <input
                    className="prep-section-title-input"
                    value={section.title}
                    onChange={(e) => renameSection(secIdx, e.target.value)}
                    data-testid={`rg-prep-sec-title-${section.id}`}
                  />
                  <div className="prep-section-actions">
                    <button type="button" onClick={() => moveSection(secIdx, -1)} disabled={secIdx === 0} title="Monter">↑</button>
                    <button type="button" onClick={() => moveSection(secIdx, 1)} disabled={secIdx === sections.length - 1} title="Descendre">↓</button>
                    <button type="button" onClick={() => deleteSection(secIdx)} className="danger" title="Supprimer la section">🗑</button>
                  </div>
                </div>

                {openIds[section.id] && (
                  <div className="prep-section-body">
                    {(section.groups || []).map((group, gIdx) => (
                      <div className="prep-group" key={gIdx}>
                        <div className="prep-group-header">
                          <input
                            className="prep-group-title-input"
                            value={group.title || ""}
                            onChange={(e) => renameGroup(secIdx, gIdx, e.target.value)}
                            placeholder="Titre de la sous-section"
                            data-testid={`rg-prep-grp-title-${section.id}-${gIdx}`}
                          />
                          <div className="prep-group-actions">
                            <button type="button" onClick={() => moveGroup(secIdx, gIdx, -1)} disabled={gIdx === 0}>↑</button>
                            <button type="button" onClick={() => moveGroup(secIdx, gIdx, 1)} disabled={gIdx === section.groups.length - 1}>↓</button>
                            <button type="button" onClick={() => deleteGroup(secIdx, gIdx)} className="danger">🗑</button>
                          </div>
                        </div>
                        <ul className="prep-items">
                          {(group.items || []).map((item, iIdx) => (
                            <li key={iIdx} className="prep-item">
                              <input
                                className="prep-item-input"
                                value={item}
                                onChange={(e) => renameItem(secIdx, gIdx, iIdx, e.target.value)}
                                placeholder="Élément"
                                data-testid={`rg-prep-item-${section.id}-${gIdx}-${iIdx}`}
                              />
                              <div className="prep-item-actions">
                                <button type="button" onClick={() => moveItem(secIdx, gIdx, iIdx, -1)} disabled={iIdx === 0}>↑</button>
                                <button type="button" onClick={() => moveItem(secIdx, gIdx, iIdx, 1)} disabled={iIdx === group.items.length - 1}>↓</button>
                                <button type="button" onClick={() => deleteItem(secIdx, gIdx, iIdx)} className="danger">🗑</button>
                              </div>
                            </li>
                          ))}
                        </ul>
                        <button
                          type="button"
                          className="rg-btn tiny ghost"
                          onClick={() => addItem(secIdx, gIdx)}
                          data-testid={`rg-prep-item-add-${section.id}-${gIdx}`}
                        >
                          + Ajouter un élément
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="rg-btn tiny ghost"
                      style={{ marginTop: 4 }}
                      onClick={() => addGroup(secIdx)}
                      data-testid={`rg-prep-grp-add-${section.id}`}
                    >
                      + Ajouter une sous-section
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="prep-footer">
            <button type="button" className="rg-btn ghost" onClick={addSection} data-testid="rg-prep-sec-add">
              + Ajouter une section
            </button>
            <button type="button" className="rg-btn ghost danger" onClick={resetDefaults} data-testid="rg-prep-reset">
              ↺ Restaurer la liste par défaut
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function PrepPreview({ sections, onClose, onPrint }) {
  return (
    <div className="prep-preview" data-testid="rg-prep-preview">
      <div className="prep-preview-toolbar">
        <button type="button" className="rg-btn ghost" onClick={onClose}>← Retour à l'édition</button>
        <button type="button" className="rg-btn primary" onClick={onPrint}>🖨️ Imprimer</button>
      </div>
      <div className="prep-preview-page">
        <h1 className="prep-preview-title">Liste préparation jour J</h1>
        <p className="prep-preview-sub">Curieux Refuge · Check-list de préparation</p>
        {sections.map((s) => (
          <section key={s.id} className="prep-preview-section">
            <h2>{s.title}</h2>
            {(s.groups || []).map((g, gi) => (
              <div key={gi} className="prep-preview-group">
                {g.title && <h3>{g.title}</h3>}
                <ul>
                  {(g.items || []).filter((it) => (it || "").trim()).map((it, ii) => (
                    <li key={ii}><span className="check-box" /> {it}</li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        ))}
      </div>
    </div>
  );
}
